<template>
  <div class="submenu-node-wrapper">
    <!-- Submódulo contenedor que tiene más submódulos adentro (Muñeca Rusa) -->
    <div v-if="tieneHijos" class="submenu-parent-group">
      <button
        @click="toggleAbierto"
        class="submenu-btn submenu-btn-parent"
        :class="{
          'submenu-btn-open': abierto,
          'nivel-1': nivel === 1,
          'nivel-2': nivel >= 2
        }"
      >
        <div class="submenu-btn-content">
          <Icon
            :icon="item.icono || (abierto ? 'mdi:folder-open' : 'mdi:folder')"
            width="15"
            height="15"
            class="submenu-parent-icon"
          />
          <span class="submenu-title">{{ item.nombre }}</span>
        </div>
        <!-- Flecha indicadora: apunta a la derecha si está cerrado, abajo si está abierto -->
        <Icon
          icon="mdi:chevron-right"
          width="15"
          height="15"
          class="submenu-chevron"
          :class="{ 'rotate-90': abierto }"
        />
      </button>

      <!-- Submódulos interiores (solo se abren cuando el usuario hace clic) -->
      <div v-show="abierto" class="submenu-content" :class="`nivel-${nivel}`">
        <SubMenuNode
          v-for="hijo in item.hijos"
          :key="hijo.id"
          :item="hijo"
          :nivel="nivel + 1"
        />
      </div>
    </div>

    <!-- Enlace final directo (sin hijos) -->
    <router-link
      v-else
      :to="item.ruta || '#'"
      class="submenu-link"
      :class="{ 'submenu-link-active': isActive(item.ruta) }"
    >
      <Icon :icon="item.icono || 'mdi:circle-small'" width="14" height="14" class="submenu-link-icon" />
      <span class="submenu-link-text">{{ item.nombre }}</span>
    </router-link>
  </div>
</template>

<script>
import { Icon } from '@iconify/vue';

export default {
  name: 'SubMenuNode',
  components: { Icon },
  props: {
    item:  { type: Object, required: true },
    nivel: { type: Number, default: 1 }
  },
  data() {
    return {
      // Inicia cerrado para que no se desplieguen todos a la vez
      abierto: false
    };
  },
  computed: {
    tieneHijos() {
      return Array.isArray(this.item?.hijos) && this.item.hijos.length > 0;
    }
  },
  methods: {
    toggleAbierto() {
      this.abierto = !this.abierto;
    },
    isActive(route) {
      if (!route) return false;
      return this.$route.path === route;
    }
  }
};
</script>

<style scoped>
.submenu-node-wrapper {
  width: 100%;
}

/* =======================================================
   SUBMÓDULOS CON HIJOS (ALTO CONTRASTE - MODO CLARO Y OSCURO)
   Colores nítidos y vivos para máxima visibilidad
   ======================================================= */
.submenu-btn-parent {
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  padding: 6.5px 10px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
  text-align: left;
  border: none;
  transition: all 0.15s ease;
  touch-action: manipulation;

  /* MODO CLARO: Borde sólido y texto oscuro de alto contraste */
  color: #0f172a; /* Casi negro para máxima nitidez */
  background: #e0e7ff; /* Fondo lavanda/índigo sólido y claramente visible */
  border-left: 4px solid #4338ca; /* Borde izquierdo índigo intenso */
}

.submenu-btn-parent .submenu-parent-icon,
.submenu-btn-parent .submenu-chevron {
  color: #4338ca; /* Icono y flecha de color vivo */
}

.submenu-btn-parent:hover {
  background: #c7d2fe;
  color: #000000;
  border-left-color: #312e81;
}

.submenu-btn-parent.submenu-btn-open {
  background: #ddd6fe !important;
  color: #000000 !important;
  border-left-color: #7c3aed !important;
}

.submenu-btn-parent.submenu-btn-open .submenu-parent-icon,
.submenu-btn-parent.submenu-btn-open .submenu-chevron {
  color: #7c3aed !important;
}

/* Nivel 2 o más anidado (azul cian vivo) */
.submenu-btn-parent.nivel-2 {
  color: #0f172a;
  background: #e0f2fe;
  border-left: 4px solid #0284c7;
}

.submenu-btn-parent.nivel-2 .submenu-parent-icon,
.submenu-btn-parent.nivel-2 .submenu-chevron {
  color: #0284c7;
}

.submenu-btn-parent.nivel-2:hover {
  background: #bae6fd;
  color: #000000;
}

.submenu-btn-parent.nivel-2.submenu-btn-open {
  background: #dcfce7 !important;
  color: #052e16 !important;
  border-left-color: #16a34a !important;
}

.submenu-btn-parent.nivel-2.submenu-btn-open .submenu-parent-icon,
.submenu-btn-parent.nivel-2.submenu-btn-open .submenu-chevron {
  color: #16a34a !important;
}

/* =======================================================
   MODO OSCURO (MODO NEGRO): Brillo y contraste sobresaliente
   ======================================================= */
.dark .submenu-btn-parent {
  color: #ffffff !important; /* Texto blanco puro para no forzar la vista */
  background: rgba(99, 102, 241, 0.32); /* Fondo iluminado */
  border-left: 4px solid #818cf8; /* Borde brillante eléctrico */
}

.dark .submenu-btn-parent .submenu-parent-icon,
.dark .submenu-btn-parent .submenu-chevron {
  color: #a5b4fc !important; /* Iconos y flechas brillantes */
}

.dark .submenu-btn-parent:hover {
  background: rgba(99, 102, 241, 0.5) !important;
  color: #ffffff !important;
  border-left-color: #a5b4fc !important;
}

.dark .submenu-btn-parent.submenu-btn-open {
  background: rgba(139, 92, 246, 0.45) !important;
  color: #ffffff !important;
  border-left-color: #c084fc !important;
}

.dark .submenu-btn-parent.submenu-btn-open .submenu-parent-icon,
.dark .submenu-btn-parent.submenu-btn-open .submenu-chevron {
  color: #c084fc !important;
}

.dark .submenu-btn-parent.nivel-2 {
  color: #ffffff !important;
  background: rgba(14, 165, 233, 0.3) !important;
  border-left: 4px solid #38bdf8 !important;
}

.dark .submenu-btn-parent.nivel-2 .submenu-parent-icon,
.dark .submenu-btn-parent.nivel-2 .submenu-chevron {
  color: #38bdf8 !important;
}

.dark .submenu-btn-parent.nivel-2:hover {
  background: rgba(14, 165, 233, 0.45) !important;
}

/* Contenido del botón */
.submenu-btn-content {
  display: flex;
  align-items: center;
  gap: 7px;
  flex: 1;
  min-width: 0;
}

.submenu-parent-icon {
  flex-shrink: 0;
}

.submenu-title {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* Flecha rotativa */
.submenu-chevron {
  transition: transform 0.2s ease;
  flex-shrink: 0;
}

.rotate-90 {
  transform: rotate(90deg);
}

/* =======================================================
   LÍNEAS GUÍA DE ANIDACIÓN (VIVAS Y CLARAS)
   ======================================================= */
.submenu-content {
  margin-left: 10px;
  padding-left: 6px;
  border-left: 2px solid #818cf8;
}

.submenu-content.nivel-2 {
  border-left-color: #38bdf8;
}

.dark .submenu-content {
  border-left-color: #818cf8;
}

.dark .submenu-content.nivel-2 {
  border-left-color: #38bdf8;
}

/* =======================================================
   ENLACE FINAL (SIN HIJOS)
   ======================================================= */
.submenu-link {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 5px 10px;
  font-size: 12px;
  text-decoration: none;
  transition: all 0.15s ease;
  touch-action: manipulation;

  /* Modo claro: Texto oscuro bien legible */
  color: #1e293b;
}

.submenu-link-icon {
  flex-shrink: 0;
  color: #64748b;
}

.submenu-link-text {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.submenu-link:hover {
  background: #f1f5f9;
  color: #000000;
}

.submenu-link:hover .submenu-link-icon {
  color: #4f46e5;
}

.submenu-link-active {
  background: #4f46e5 !important;
  color: #ffffff !important;
  font-weight: 700;
}

.submenu-link-active .submenu-link-icon {
  color: #ffffff !important;
}

/* Modo oscuro enlaces */
.dark .submenu-link {
  color: #f8fafc;
}

.dark .submenu-link-icon {
  color: #94a3b8;
}

.dark .submenu-link:hover {
  background: #334155;
  color: #ffffff;
}

.dark .submenu-link:hover .submenu-link-icon {
  color: #818cf8;
}

.dark .submenu-link-active {
  background: #6366f1 !important;
  color: #ffffff !important;
}

.dark .submenu-link-active .submenu-link-icon {
  color: #ffffff !important;
}
</style>
